"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Compass, Search, ShieldCheck, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : typeof window !== "undefined" ? (window as any).wvcClient : undefined;
interface TopicGuide {
  title: string;
  desc: string;
  tag: string;
}
interface WpCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}
interface SectionContent {
  eyebrow: string;
  badgeVerified: string;
  badgeRatio: string;
  heading: string;
  subtitle: string;
  description: string;
  topics: TopicGuide[];
  ctaAssessment: string;
  ctaCurriculum: string;
  searchPlaceholder: string;
  searchBtn: string;
  categoriesLabel: string;
}
const CONTENT: Record<"vi" | "en", SectionContent> = {
  vi: {
    eyebrow: "Cẩm nang & Tài nguyên Phụ huynh",
    badgeVerified: "Chứng nhận lộ trình Lab",
    badgeRatio: "Tỷ lệ 1 Mentor : 6 Học viên",
    heading: "Mọi điều cha mẹ cần để đồng hành cùng hành trình Engineering của con.",
    subtitle: "Lộ trình rõ ràng, so sánh chương trình đào tạo và lời khuyên thực tiễn từ các giám đốc học thuật ROBOTO.",
    description: "Việc lựa chọn hướng đi công nghệ phù hợp có thể khiến phụ huynh băn khoăn trước sự phát triển thần tốc của AI và Robotics. Đội ngũ chuyên môn tại ROBOTO đã xây dựng thư viện tài nguyên này nhằm làm sáng tỏ từng cột mốc STEM, so sánh các bộ công cụ phần hardware thực hành, giúp cha mẹ tự tin nuôi dưỡng niềm say mê sáng tạo của con ngay tại nhà.",
    topics: [{
      title: "So sánh chương trình đào tạo",
      desc: "Đối chiếu độ tuổi và năng lực của con với các mô hình lắp ráp LEGO nền tảng hoặc lập trình Python Robotics nâng cao.",
      tag: "Độ tuổi 4–16"
    }, {
      title: "Bí quyết học tập tại nhà",
      desc: "Phát triển tư duy máy tính, giải đố logic và tìm hiểu mạch điện tử căn bản một cách an toàn tại gia đình.",
      tag: "Thói quen thực tiễn"
    }, {
      title: "Lộ trình thi đấu & giải đấu",
      desc: "Nắm vững các bảng đấu giải, kế hoạch cố vấn chuyên sâu và kỹ năng làm việc nhóm trong đấu trường Robotics.",
      tag: "Chuẩn bị thi đấu"
    }],
    ctaAssessment: "Đăng ký đánh giá năng lực miễn phí",
    ctaCurriculum: "Khám phá lộ trình học tập",
    searchPlaceholder: "Tìm kiếm bài viết, Robotics, STEM, Coding…",
    searchBtn: "Tìm kiếm",
    categoriesLabel: "Chủ đề bài viết"
  },
  en: {
    eyebrow: "Parent Guide & Resources",
    badgeVerified: "Lab Track verified",
    badgeRatio: "1:6 Mentor Ratio",
    heading: "Everything you need to guide their engineering journey.",
    subtitle: "Clear roadmaps, curriculum comparisons, and practical advice from ROBOTO's academic directors.",
    description: "Choosing the right technical path can feel overwhelming with rapid developments in artificial intelligence and robotics. Our faculty assembled this dedicated resource library to demystify STEM milestones, compare hands-on hardware toolkits, and help you support your child's curiosity with confidence at home.",
    topics: [{
      title: "Curriculum comparisons",
      desc: "Match your child's age and experience with foundational LEGO builds or advanced Python robotics.",
      tag: "Ages 4–16"
    }, {
      title: "At-home learning tips",
      desc: "Reinforce computational thinking, logic puzzles, and basic electronics safely from home.",
      tag: "Practical habits"
    }, {
      title: "Competition roadmaps",
      desc: "Understand tournament tracks, mentorship timelines, and teamwork requirements for robotics leagues.",
      tag: "League prep"
    }],
    ctaAssessment: "Book a free assessment",
    ctaCurriculum: "Explore curriculum paths",
    searchPlaceholder: "Search articles, Robotics, STEM, Coding…",
    searchBtn: "Search",
    categoriesLabel: "Article topics"
  }
};
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy mascot giving thumbs up"
};
export default function ParentResourcesIntro() {
  const prefersReducedMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<WpCategory[]>([]);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage availability errors in private/sandboxed frames
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const client = getWvcClient();
        let rawData: any = null;
        if (client?.pluginRequest) {
          try {
            rawData = await client.pluginRequest("wp/v2/categories?hide_empty=true&per_page=50");
          } catch {
            // fallback if pluginRequest doesn't route wp/v2
          }
        }
        if (!rawData) {
          const resp = await fetch("/wp-json/wp/v2/categories?hide_empty=true&per_page=50");
          if (resp.ok) {
            rawData = await resp.json();
          }
        }
        const list = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : [];
        const validCategories: WpCategory[] = list.filter((c: any) => typeof c === "object" && c !== null && Number(c.count) > 0).map((c: any) => ({
          id: c.id,
          name: typeof c.name === "string" ? c.name.replace(/&amp;/g, "&").replace(/&#038;/g, "&") : "",
          slug: c.slug || "",
          count: Number(c.count) || 0
        })).filter((c: WpCategory) => c.name && c.slug);
        if (isMounted) {
          setCategories(validCategories);
        }
      } catch {
        // Leave categories empty on failure
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query) {
      window.location.href = `/?s=${encodeURIComponent(query)}`;
    }
  };
  const t = CONTENT[lang];
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1,
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="welcome-to-parent-resources" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-20 lg:py-24 text-foreground isolate">
      {/* Precision Spec Rail motif */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 xl:px-16 2xl:px-[240px]">
        <div className="relative mb-8 flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-none bg-secondary" />
          <div className="h-px w-24 bg-secondary" />
          <span className="font-mono text-xs font-semibold tracking-wider text-secondary uppercase">
            {t.eyebrow}
          </span>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Photograph of parent and child examining a robotic vehicle */}
          <motion.div variants={itemVariants} className="lg:col-span-5 xl:col-span-6">
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              {/* Outer decorative card container */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-lg">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_child_robotics_lab_workshop.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_child_robotics_lab_workshop.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/parent_child_robotics_lab_workshop.png-wvc-sizes" alt="Parent and child examining a student-built robotic vehicle in natural daylight" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
                </div>

                {/* Floating Technical Milestone Badge with subtle pastel accent */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-2 pb-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex size-2 rounded-full bg-secondary" />
                    <span className="font-mono text-xs font-medium text-muted-foreground uppercase">
                      {t.badgeVerified}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 font-mono text-xs font-medium text-foreground">
                    <ShieldCheck className="size-3.5 text-secondary" />
                    <span>{t.badgeRatio}</span>
                  </div>
                </div>
              </div>

              {/* Decorative engineering accent block */}
              <div className="absolute -bottom-4 -right-4 -z-10 h-28 w-28 rounded-2xl border border-border/80 bg-muted/40 hidden sm:block" />
            </div>
          </motion.div>

          {/* Right Column: Main heading, supporting line, narrative, and guidance modules */}
          <motion.div variants={itemVariants} className="lg:col-span-7 xl:col-span-6">
            <div className="flex flex-col">
              <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {t.heading}
              </h1>

              <p className="mt-4 text-lg font-medium text-foreground/90 sm:text-xl">
                {t.subtitle}
              </p>

              <p className="mt-4 leading-relaxed text-muted-foreground">
                {t.description}
              </p>

              {/* Quick-reference topic items */}
              <div className="mt-8 space-y-3.5">
                {t.topics.map((topic, i) => <div key={topic.title} data-index={i} className="flex flex-col gap-1 rounded-lg border border-border bg-card/60 p-4 transition-colors hover:border-secondary hover:bg-card">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="font-serif text-base font-semibold text-foreground">
                        {topic.title}
                      </h2>
                      <span className="rounded-full bg-muted px-2.5 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground uppercase">
                        {topic.tag}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {topic.desc}
                    </p>
                  </div>)}
              </div>

              {/* Search and Category Navigation */}
              <div className="mt-6 space-y-4">
                {/* Search Form */}
                <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <input type="search" name="s" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t.searchPlaceholder} className="w-full rounded-lg border border-input bg-card pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20" />
                  </div>
                  <Button type="submit" className="rounded-lg bg-accent px-5 py-3 font-serif font-semibold text-accent-foreground shadow-sm transition-all hover:bg-accent/90 shrink-0">
                    <Search className="size-4" aria-hidden="true" />
                    <span>{t.searchBtn}</span>
                  </Button>
                </form>

                {/* Dynamic Category Navigation Chips */}
                {categories.length > 0 && <div className="flex flex-col gap-2 pt-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground font-mono">
                      <Tag className="size-3.5 text-secondary" aria-hidden="true" />
                      <span>{t.categoriesLabel}:</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {categories.map((cat, i) => <Link key={cat.id || cat.slug} data-index={i} to={`/ParentGuideResourcesPage?category=${encodeURIComponent(cat.slug)}#latest-articles-guides`} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-secondary hover:bg-secondary/10 hover:text-secondary">
                          <span>{cat.name}</span>
                          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                            {cat.count}
                          </span>
                        </Link>)}
                    </div>
                  </div>}
              </div>

              {/* Primary action triggers */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild className="rounded-lg bg-accent px-6 py-3 font-serif font-semibold text-accent-foreground shadow-sm transition-all hover:bg-accent/90">
                  <Link to="/FreeTrialContactPage">
                    {t.ctaAssessment}
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="rounded-lg border-border font-serif font-semibold text-foreground hover:bg-muted">
                  <Link to="/LearningPathCurriculumPage">
                    <Compass className="size-4 text-secondary" />
                    {t.ctaCurriculum}
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Static floating decorative mascot visual */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute z-0 hidden lg:block lg:bottom-6 lg:left-8 xl:bottom-8 xl:left-12 2xl:left-20">
        <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt={DEFAULT_MASCOT.alt} loading="lazy" decoding="async" className="h-28 w-28 lg:h-32 lg:w-32 xl:h-36 xl:w-36 bg-transparent object-contain drop-shadow-md pointer-events-none select-none" />
      </div>
    </section>;
}